import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Card, CardContent } from '@/components/ui/card';
import { Search, Trophy, Medal, Star, CalendarDays, TrendingUp, Package, ChevronDown, ChevronUp } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import CustomDatePicker from '@/components/shared/CustomDatePicker';

const fmt = (n) => new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

export default function RankingOmset() {
  const [dateMode, setDateMode] = useState('all'); // 'all' or 'custom'
  const [statusFilterMode, setStatusFilterMode] = useState('all'); // 'all' (Semua PO) or 'completed' (Hanya PO Selesai/Lunas)
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedPic, setExpandedPic] = useState(null);

  // Fetch all POs specifically for ranking (bypassing useClientPOs filter)
  const { data: rawPOs = [], isLoading } = useQuery({
    queryKey: ['all-client-pos-ranking'],
    queryFn: async () => {
      const data = await base44.entities.ClientPO.list('-created_date');
      return data.filter(po => !po._isManualReceivable && po.status !== 'cancelled');
    },
    staleTime: 5000,
  });

  const { data: productionOrders = [] } = useQuery({
    queryKey: ['production-orders-ranking'],
    queryFn: () => base44.entities.ProductionOrder.list(),
  });

  const rankings = useMemo(() => {
    // 1. Filter by date
    let filtered = rawPOs;
    if (dateMode === 'custom' && startDate && endDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      
      filtered = filtered.filter(po => {
        const poMatch = productionOrders.find(p => String(p.order_number) === String(po.id));
        const dStr = po.surat_jalan_date || poMatch?.shipping_date || poMatch?.shipped_at || po.date || po.created_date;
        const d = new Date(dStr);
        return d >= start && d <= end;
      });
    }

    // 2. Filter by PO Status mode
    if (statusFilterMode === 'completed') {
      filtered = filtered.filter(po => {
        const poMatch = productionOrders.find(p => String(p.order_number) === String(po.id));
        const st = (po.status || po.status_po || po.status_produksi || poMatch?.status || poMatch?.status_produksi || '').toLowerCase();
        const isLunas = po.statusBayar === 'Lunas' || po.status_pembayaran === 'Lunas' || po.is_lunas;
        const isSelesai = st.includes('selesai') || st.includes('dikirim') || st.includes('shipped') || st.includes('lunas');
        return isSelesai || isLunas;
      });
    }

    // 2. Group by PIC
    const picMap = {};
    filtered.forEach(po => {
      const picName = po.pic || 'Tanpa PIC';
      const brandName = po.customerName ? po.customerName.split('/')[0].trim().toUpperCase() : 'SATALA';
      
      let rawProducts = po.products || [];
      if (!Array.isArray(rawProducts)) {
        try { rawProducts = JSON.parse(rawProducts); } catch(e) { rawProducts = []; }
        if (!Array.isArray(rawProducts)) rawProducts = [];
      }
      
      let poProdHpp = 0;
      let totalJasa = 0;
      rawProducts.forEach(p => {
        const qty = p.qty || 1;
        const jasaNominal = p.jasa_nominal || p.final_jasa_nominal || 0;
        totalJasa += (jasaNominal * qty);
        
        if (p.total_hpp_product !== undefined && Number(p.total_hpp_product) > 0) {
          poProdHpp += Number(p.total_hpp_product);
        } else {
          const modalPackaging = (p.botol_harga_beli || 0) + (p.tutup_harga_beli || 0) + (p.spray_harga_beli || 0) + (p.fullset_harga_beli || 0);
          const volume = p.botol_ukuran_aktual || p.botol_ukuran_label || p.botol_ukuran_client || p.ukuran_botol_ml || p.volume_ml || 0;
          
          let racikan = p.racikan || [];
          if (!Array.isArray(racikan)) {
            try { racikan = JSON.parse(racikan); } catch(e) { racikan = []; }
            if (!Array.isArray(racikan)) racikan = [];
          }
          const modalRacikan = racikan.reduce((rs, r) => {
            const costPerUnit = r.hpp_bahan || r.harga_per_satuan || 0;
            const volPerPcs = volume * ((parseFloat(String(r.persentase).replace(',', '.')) || 0) / 100);
            return rs + volPerPcs * (costPerUnit / 1000);
          }, 0);
          
          let komponenTambahan = p.komponen_tambahan || [];
          if (!Array.isArray(komponenTambahan)) {
            try { komponenTambahan = JSON.parse(komponenTambahan); } catch(e) { komponenTambahan = []; }
            if (!Array.isArray(komponenTambahan)) komponenTambahan = [];
          }
          const modalKomponen = komponenTambahan.reduce((ks, k) => ks + (k.harga || 0), 0);
          const customHargaBeli = p.hargaBeli || p.harga_beli || 0;
          
          const hppPerPcs = modalPackaging + modalRacikan + modalKomponen + jasaNominal + customHargaBeli;
          poProdHpp += (hppPerPcs * qty);
        }
      });
      
      let legHpp = (po.legalitas || []).reduce((s, l) => s + ((l.harga_beli || 0) * (l.qty_variant || 1)), 0);

      // Calculate Omset exactly like FinanceInvoices
      const prodJual = (po.products || []).reduce((s, p) => s + (p.total_harga_jual || p.subtotal || p.hargaTotal || 0), 0);
      const legJual = (po.legalitas || []).reduce((s, l) => s + (l.harga_jual * (l.qty_variant || 1)), 0);
      const nominalPPN = po.ppn_mode === 'exclude' ? ((prodJual + legJual) * (parseFloat(po.ppn_percentage) || 0) / 100) : 0;
      
      const invoiceOmset = prodJual + legJual; // Omset Murni
      
      // Jasa is pure profit, so we remove it from HPP for accurate margin
      const hppTanpaJasa = (poProdHpp + legHpp) - totalJasa;
      const poMargin = invoiceOmset - hppTanpaJasa;

      if (!picMap[picName]) {
        picMap[picName] = { name: picName, totalOmset: 0, totalMargin: 0, totalProduksi: 0, totalLegalitas: 0, brands: {} };
      }
      picMap[picName].totalOmset += invoiceOmset;
      picMap[picName].totalMargin += poMargin;
      picMap[picName].totalProduksi += prodJual;
      picMap[picName].totalLegalitas += legJual;

      if (!picMap[picName].brands[brandName]) {
        picMap[picName].brands[brandName] = { omset: 0, margin: 0 };
      }
      picMap[picName].brands[brandName].omset += invoiceOmset;
      picMap[picName].brands[brandName].margin += poMargin;
    });

    // 3. Convert to array and format
    let rankArray = Object.values(picMap).map(p => {
      const brandList = Object.entries(p.brands)
        .map(([bName, bData]) => ({ name: bName, omset: bData.omset, margin: bData.margin }))
        .sort((a, b) => b.omset - a.omset);
      
      return {
        ...p,
        brandList
      };
    });

    // 4. Sort by totalOmset descending
    rankArray.sort((a, b) => b.totalOmset - a.totalOmset);

    // 5. Apply Search Filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      rankArray = rankArray.filter(p => 
        p.name.toLowerCase().includes(q) ||
        p.brandList.some(b => b.name.toLowerCase().includes(q))
      );
    }

    return rankArray;
  }, [rawPOs, dateMode, statusFilterMode, startDate, endDate, searchQuery, productionOrders]);

  const totalGlobalOmset = rankings.reduce((sum, r) => sum + r.totalOmset, 0);
  const totalGlobalMargin = rankings.reduce((sum, r) => sum + r.totalMargin, 0);
  const globalMarginPercentage = totalGlobalOmset > 0 ? (totalGlobalMargin / totalGlobalOmset) * 100 : 0;

  return (
    <div className="space-y-6 lg:space-y-8 pb-16 text-[#302621]">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 border-b border-[#E6D5B8]/40 pb-5">
        <div className="flex flex-col gap-1.5">
          <h1 className="text-xl md:text-3xl font-black text-slate-800 tracking-tight flex items-center gap-3">
            <Trophy className="w-6 h-6 md:w-8 md:h-8 text-[#D4AF37]" /> Ranking Omset Marketing
          </h1>
          <p className="text-[10px] md:text-xs text-slate-500 font-medium max-w-xl leading-relaxed">
            Papan peringkat omset dari seluruh tim Marketing. Pilihan 2 mode perhitungan omset tersedia di filter toolbar. Ayo kejar targetmu! 🚀
          </p>
        </div>
        
        {/* Global Stats */}
        <div className="flex flex-col md:flex-row gap-3 shrink-0">
          <div className="bg-[#F9F5EC] border border-[#E6D5B8] rounded-2xl px-5 py-3 shadow-sm flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-[#8C6208]" />
            </div>
            <div>
              <p className="text-[9px] md:text-[10px] font-bold text-[#8C6208] uppercase tracking-wider">Total Omset</p>
              <p className="text-lg md:text-xl font-black text-[#302621]">Rp {fmt(totalGlobalOmset)}</p>
            </div>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl px-5 py-3 shadow-sm flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
              <span className="font-bold text-emerald-700">%</span>
            </div>
            <div>
              <p className="text-[9px] md:text-[10px] font-bold text-emerald-700 uppercase tracking-wider">Total Margin</p>
              <p className="text-lg md:text-xl font-black text-emerald-800">Rp {fmt(totalGlobalMargin)} <span className="text-xs font-bold text-emerald-600 bg-emerald-100/50 px-1.5 py-0.5 rounded-md ml-1">{globalMarginPercentage.toFixed(1)}%</span></p>
            </div>
          </div>
        </div>
      </div>

      {/* Mode Perhitungan Omset Toggle & Filters */}
      <div className="flex flex-col gap-4 bg-white/80  border border-slate-200/60 p-4 rounded-3xl shadow-sm">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-slate-50/70 border border-slate-200/80 p-2 rounded-2xl">
          <span className="text-xs font-bold text-slate-600 px-2 flex items-center gap-1.5">
            <Package className="w-4 h-4 text-[#8C6208]" /> Mode Hitung Omset:
          </span>
          <div className="flex items-center gap-1.5 w-full sm:w-auto">
            <button
              type="button"
              onClick={() => setStatusFilterMode('all')}
              className={`flex-1 sm:flex-initial px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                statusFilterMode === 'all'
                  ? 'bg-[#302621] text-white shadow-md'
                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
              }`}
            >
              📊 Semua PO & Invoice (Potensial & Berjalan)
            </button>
            <button
              type="button"
              onClick={() => setStatusFilterMode('completed')}
              className={`flex-1 sm:flex-initial px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                statusFilterMode === 'completed'
                  ? 'bg-emerald-600 text-white shadow-md'
                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-emerald-50 hover:text-emerald-700'
              }`}
            >
              ✅ Hanya PO Selesai & Lunas (Omset Realized)
            </button>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-4 items-center">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input 
              type="text"
              placeholder="Cari nama marketing atau brand..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-11 bg-slate-50/50 border-slate-200 rounded-xl"
            />
          </div>
          
          <div className="flex items-center gap-2 w-full md:w-auto">
            <Button 
              variant={dateMode === 'all' ? 'default' : 'outline'}
              onClick={() => setDateMode('all')}
              className={`h-11 rounded-xl px-6 transition-all ${dateMode === 'all' ? 'bg-[#302621] hover:bg-[#302621]/90 shadow-md' : 'border-slate-200 hover:bg-slate-50'}`}
            >
            All Time
          </Button>

          <Popover>
            <PopoverTrigger asChild>
              <Button 
                variant={dateMode === 'custom' ? 'default' : 'outline'}
                onClick={() => setDateMode('custom')}
                className={`h-11 rounded-xl px-4 flex items-center gap-2 transition-all ${dateMode === 'custom' ? 'bg-[#D4AF37] text-[#302621] hover:bg-[#D4AF37]/90 font-bold border-transparent shadow-md' : 'border-slate-200 hover:bg-slate-50'}`}
              >
                <CalendarDays className="w-4 h-4" />
                {dateMode === 'custom' && startDate && endDate ? `${startDate} s/d ${endDate}` : 'Tanggal Tertentu'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-4 rounded-2xl shadow-xl border-slate-200" align="end">
              <div className="space-y-4">
                <h4 className="font-bold text-sm text-slate-800">Pilih Rentang Tanggal</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-slate-500 uppercase">Mulai</label>
                    <CustomDatePicker value={startDate} onChange={(val) => { setStartDate(val); setDateMode('custom'); }} className="h-9 text-xs" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-slate-500 uppercase">Sampai</label>
                    <CustomDatePicker value={endDate} onChange={(val) => { setEndDate(val); setDateMode('custom'); }} className="h-9 text-xs" />
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
      </div>

      {/* Leaderboard */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-white rounded-3xl border border-slate-100"></div>
            ))}
          </div>
        ) : rankings.length === 0 ? (
          <div className="text-center py-20 bg-white/50 border border-dashed border-slate-200 rounded-3xl">
            <Trophy className="w-16 h-16 text-slate-200 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-slate-500">Belum Ada Data Omset</h3>
            <p className="text-sm text-slate-400 mt-1">Coba ubah filter pencarian atau tanggal.</p>
          </div>
        ) : (
          rankings.map((rank, idx) => {
            const isTop3 = idx < 3;
            const isExpanded = expandedPic === rank.name;
            
            // Premium Styling for Top 3
            let cardStyle = "bg-white border-slate-200 hover:border-[#D4AF37]/50";
            let rankBadge = "bg-slate-100 text-slate-500";
            let icon = null;
            
            if (idx === 0) {
              cardStyle = "bg-gradient-to-r from-amber-50 to-[#FFFDF9] border-amber-200 shadow-md shadow-amber-100/50";
              rankBadge = "bg-amber-400 text-white shadow-sm shadow-amber-200";
              icon = <Trophy className="w-5 h-5 text-white" />;
            } else if (idx === 1) {
              cardStyle = "bg-gradient-to-r from-slate-50 to-white border-slate-300 shadow-sm";
              rankBadge = "bg-slate-300 text-slate-700 shadow-sm";
              icon = <Medal className="w-5 h-5 text-slate-700" />;
            } else if (idx === 2) {
              cardStyle = "bg-gradient-to-r from-orange-50 to-white border-orange-200 shadow-sm";
              rankBadge = "bg-orange-300 text-orange-800 shadow-sm";
              icon = <Medal className="w-5 h-5 text-orange-800" />;
            }

            return (
              <Card 
                key={rank.name} 
                className={`overflow-hidden transition-all duration-300 rounded-[2rem] border ${cardStyle}`}
              >
                <CardContent className="p-0">
                  <div 
                    className="p-5 flex items-center gap-4 cursor-pointer"
                    onClick={() => setExpandedPic(isExpanded ? null : rank.name)}
                  >
                    {/* Rank Badge */}
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-black text-lg shrink-0 ${rankBadge}`}>
                      {icon ? icon : `#${idx + 1}`}
                    </div>
                    
                    {/* Marketing Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base md:text-xl font-black text-slate-800 truncate">{rank.name}</h3>
                      <p className="text-[10px] md:text-xs font-medium text-slate-500 mt-0.5 flex items-center gap-1.5">
                        <Package className="w-3.5 h-3.5" /> {rank.brandList.length} Brand Aktif
                      </p>
                    </div>

                    {/* Omset Total */}
                    <div className="text-right">
                      <p className="text-[9px] md:text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Total Omset</p>
                      <p className={`text-lg md:text-2xl font-black tracking-tight ${idx === 0 ? 'text-[#8C6208]' : 'text-slate-800'}`}>
                        Rp {fmt(rank.totalOmset)}
                      </p>
                    </div>
                    
                    {/* Margin Total */}
                    <div className="text-right ml-4 pl-4 border-l border-slate-200/60 hidden sm:block">
                      <p className="text-[9px] md:text-[10px] font-bold text-emerald-600/80 uppercase tracking-wider mb-0.5">Total Margin</p>
                      <p className="text-base md:text-xl font-black text-emerald-700 tracking-tight">
                        Rp {fmt(rank.totalMargin)}
                      </p>
                      <p className="text-[10px] font-bold text-emerald-600 mt-0.5">
                        {rank.totalOmset > 0 ? ((rank.totalMargin / rank.totalOmset) * 100).toFixed(1) : 0}% margin
                      </p>
                    </div>
                    
                    <div className="ml-4 pl-4 border-l border-slate-200/60 text-slate-400 shrink-0">
                      {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </div>
                  </div>

                  {/* Expanded Breakdown */}
                  {isExpanded && (
                    <div className="bg-slate-50/80 border-t border-slate-100 p-5 px-6 pb-6">
                      
                      {/* Summary Produksi & Legalitas */}
                      <div className="flex flex-col sm:flex-row gap-4 mb-6">
                        <div className="bg-white p-3 px-4 rounded-xl border border-slate-200/60 shadow-sm flex-1 flex flex-col justify-center">
                          <p className="text-[9px] md:text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Total Omset Produksi</p>
                          <p className="text-base md:text-lg font-black text-slate-700">Rp {fmt(rank.totalProduksi)}</p>
                        </div>
                        <div className="bg-white p-3 px-4 rounded-xl border border-slate-200/60 shadow-sm flex-1 flex flex-col justify-center">
                          <p className="text-[9px] md:text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Total Omset Legalitas</p>
                          <p className="text-base md:text-lg font-black text-[#8C6208]">Rp {fmt(rank.totalLegalitas)}</p>
                        </div>
                      </div>

                      <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                        <Star className="w-4 h-4 text-[#D4AF37]" /> Rincian Omset per Brand
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {rank.brandList.map(brand => (
                          <div key={brand.name} className="flex justify-between items-center bg-white p-3 px-4 rounded-xl border border-slate-200/60 shadow-sm hover:shadow-md transition-shadow">
                            <span className="font-bold text-sm text-slate-700 truncate pr-3">{brand.name}</span>
                            <div className="flex flex-col items-end">
                              <span className="font-black text-sm text-[#8C6208] whitespace-nowrap">Rp {fmt(brand.omset)}</span>
                              <span className="text-[10px] font-bold text-emerald-600">Margin: Rp {fmt(brand.margin)} ({brand.omset > 0 ? ((brand.margin / brand.omset) * 100).toFixed(1) : 0}%)</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
